import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Globe,
  Smartphone,
  ShoppingCart,
  CheckCircle2,
  ArrowRight,
  Code2,
  Palette,
  Rocket,
  Shield,
  LifeBuoy
} from 'lucide-react';
import { services } from '../data/mock';

const iconMap = {
  Globe: Globe,
  Smartphone: Smartphone,
  ShoppingCart: ShoppingCart
};

const technologies = [
  'React', 'Next.js', 'Node.js', 'TypeScript', 'Python',
  'PostgreSQL', 'MongoDB', 'AWS', 'Docker', 'Tailwind CSS'
];

const Services = () => {
  const { t } = useTranslation();
  
  const processSteps = [
    { icon: Code2, title: t('services.step1'), description: t('services.step1Desc') },
    { icon: Palette, title: t('services.step2'), description: t('services.step2Desc') },
    { icon: Rocket, title: t('services.step3'), description: t('services.step3Desc') },
    { icon: Shield, title: t('services.step4'), description: t('services.step4Desc') },
    { icon: LifeBuoy, title: t('services.step5'), description: t('services.step5Desc') }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-300 text-sm font-medium mb-6">
              {t('services.badge')}
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              {t('services.title')}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400"> {t('services.titleHighlight')}</span>
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              {t('services.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              const isEven = index % 2 === 0;
              
              return (
                <div
                  key={service.id}
                  className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}
                >
                  {/* Image/Visual Side */}
                  <div className="flex-1 w-full">
                    <div className={`relative rounded-2xl overflow-hidden bg-gradient-to-br ${
                      index === 0 ? 'from-blue-500 to-cyan-500' :
                      index === 1 ? 'from-indigo-500 to-blue-500' :
                      'from-cyan-500 to-teal-500'
                    } p-8 aspect-[4/3] flex items-center justify-center`}>
                      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:32px_32px]" />
                      {IconComponent && (
                        <IconComponent className="relative text-white/90" size={120} strokeWidth={1} />
                      )}
                    </div>
                  </div>

                  {/* Content Side */}
                  <div className="flex-1">
                    <h2 className="text-3xl font-bold text-slate-900 mb-4">{service.title}</h2>
                    <p className="text-lg text-slate-600 mb-8">{service.description}</p>
                    <ul className="space-y-3 mb-8">
                      {service.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-3">
                          <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                            <CheckCircle2 className="text-green-600" size={16} />
                          </div>
                          <span className="text-slate-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to="/preise">
                      <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                        {t('services.viewPrices')}
                        <ArrowRight className="ml-2" size={18} />
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">{t('services.processBadge')}</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3 mb-4">
              {t('services.processTitle')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg">
              {t('services.processDescription')}
            </p>
          </div>

          <div className="relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-blue-200 -translate-y-1/2" />
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
              {processSteps.map((step, index) => (
                <div key={index} className="relative">
                  <Card className="border-0 shadow-md hover:shadow-lg transition-shadow duration-300 h-full">
                    <CardContent className="p-6 text-center">
                      <div className="relative mx-auto w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center mb-4">
                        <step.icon className="text-white" size={28} />
                        <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-white border-2 border-blue-600 text-blue-600 text-xs font-bold flex items-center justify-center">
                          {index + 1}
                        </span>
                      </div>
                      <h3 className="font-semibold text-slate-900 mb-2">{step.title}</h3>
                      <p className="text-sm text-slate-600">{step.description}</p>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">{t('services.techBadge')}</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3 mb-4">
              {t('services.techTitle')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg">
              {t('services.techDescription')}
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {technologies.map((tech, index) => (
              <div
                key={index}
                className="px-6 py-3 rounded-full bg-slate-100 text-slate-700 font-medium hover:bg-blue-100 hover:text-blue-700 transition-colors duration-200 cursor-default"
              >
                {tech}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            {t('services.ctaTitle')}
          </h2>
          <p className="text-slate-400 mb-8 text-lg">
            {t('services.ctaDescription')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/kontakt">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
                {t('services.ctaButton1')}
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <Link to="/preise">
              <Button size="lg" variant="outline" className="border-slate-600 text-white hover:bg-slate-800">
                {t('services.ctaButton2')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
